// @systeme: interface
// La feuille pour noter ou corriger une tâche. Une décision à la fois : le titre d'abord,
// le jour ensuite, l'heure seulement si on la veut. Tout est éditable au doigt.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import type { Document, Element, Membre } from '@foyer/shared';
import { api } from '../lib/api';
import { heureDe, jourDe } from '../lib/dates';
import { tailles } from '../theme/theme';

type ChoixJour = 'aujourdhui' | 'demain' | 'sans_date' | 'autre';

export interface ValeursElement {
  titre: string;
  /** null = sans date */
  jour: string | null;
  /** null = journée entière */
  heure: string | null;
  visibilite: 'espace' | 'prive';
  /** Qui s'en charge ; null = personne. Jamais demandé : dans « Plus d'options ». */
  responsableId: string | null;
}

interface Props {
  ouvert: boolean;
  onFermer(): void;
  onEnregistrer(valeurs: ValeursElement): Promise<void>;
  onSupprimer?(): Promise<void>;
  /** Les jours d'aujourd'hui et de demain, donnés par le serveur. */
  jourAujourdhui: string;
  jourDemain: string;
  /** L'élément à corriger ; absent pour une création. */
  element?: Element;
  /** Les membres du foyer : « qui s'en charge » n'apparaît que s'ils sont plusieurs. */
  membres?: Membre[];
}

function choixInitial(
  element: Element | undefined,
  aujourdhui: string,
  demain: string,
): { choix: ChoixJour; autre: string } {
  if (!element) return { choix: 'aujourdhui', autre: '' };
  if (element.debutLe === null) return { choix: 'sans_date', autre: '' };
  const jour = jourDe(element.debutLe);
  if (jour === aujourdhui) return { choix: 'aujourdhui', autre: '' };
  if (jour === demain) return { choix: 'demain', autre: '' };
  return { choix: 'autre', autre: jour };
}

export function FeuilleElement({
  ouvert,
  onFermer,
  onEnregistrer,
  onSupprimer,
  jourAujourdhui,
  jourDemain,
  element,
  membres = [],
}: Props) {
  // L'état part de l'élément à corriger (ou de rien). Le parent remonte la feuille avec
  // une nouvelle clé à chaque ouverture : pas d'état résiduel d'une fois sur l'autre.
  const initial = choixInitial(element, jourAujourdhui, jourDemain);
  const heureExistante =
    element && element.debutLe && !element.journeeEntiere ? heureDe(element.debutLe) : '';
  const [titre, setTitre] = useState(element?.titre ?? '');
  const [choix, setChoix] = useState<ChoixJour>(initial.choix);
  const [autreJour, setAutreJour] = useState(initial.autre);
  const [avecHeure, setAvecHeure] = useState(heureExistante !== '');
  const [heure, setHeure] = useState(heureExistante);
  const [prive, setPrive] = useState(element?.visibilite === 'prive');
  const [responsableId, setResponsableId] = useState<string>(element?.responsableId ?? '');
  const [photos, setPhotos] = useState<Document[]>([]);

  // Les photos rattachées (un mot d'école, une facture) : seulement pour un élément qui en a l'origine.
  useEffect(() => {
    if (!element || element.origine !== 'photo') return;
    let annule = false;
    api.documents
      .deElement(element.id)
      .then((d) => !annule && setPhotos(d))
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, [element]);
  const [plusOptions, setPlusOptions] = useState(Boolean(element?.responsableId));
  const [enCours, setEnCours] = useState(false);

  const jourChoisi =
    choix === 'aujourdhui'
      ? jourAujourdhui
      : choix === 'demain'
        ? jourDemain
        : choix === 'autre'
          ? autreJour
          : null;
  const pret =
    titre.trim().length > 0 &&
    (choix !== 'autre' || autreJour !== '') &&
    (!avecHeure || heure !== '');

  async function enregistrer() {
    if (!pret) return;
    setEnCours(true);
    try {
      await onEnregistrer({
        titre: titre.trim(),
        jour: jourChoisi,
        heure: jourChoisi !== null && avecHeure && heure !== '' ? heure : null,
        visibilite: prive ? 'prive' : 'espace',
        responsableId: responsableId === '' ? null : responsableId,
      });
      onFermer();
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Drawer anchor="bottom" open={ouvert} onClose={onFermer}>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
        sx={{ px: `${tailles.marge}px`, pt: 2, pb: 'calc(24px + env(safe-area-inset-bottom))' }}
      >
        <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
          {element ? 'Corriger' : 'Qu’est-ce que tu as en tête ?'}
        </Typography>
        <Stack spacing={2}>
          {photos.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {photos.map((p) => (
                <Box
                  key={p.id}
                  component="a"
                  href={api.documents.urlFichier(p.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Box
                    component="img"
                    src={api.documents.urlFichier(p.id)}
                    alt={p.nom}
                    sx={{ height: 96, borderRadius: 2, display: 'block' }}
                  />
                </Box>
              ))}
            </Stack>
          )}
          <TextField
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Appeler le dentiste pour Léa"
            autoFocus
            inputProps={{ maxLength: 200, 'aria-label': 'Titre' }}
          />
          <ToggleButtonGroup
            exclusive
            value={choix}
            onChange={(_e, v: ChoixJour | null) => v && setChoix(v)}
            fullWidth
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="aujourdhui" sx={{ flex: '1 1 45%' }}>
              Aujourd’hui
            </ToggleButton>
            <ToggleButton value="demain" sx={{ flex: '1 1 45%' }}>
              Demain
            </ToggleButton>
            <ToggleButton value="sans_date" sx={{ flex: '1 1 45%' }}>
              Sans date
            </ToggleButton>
            <ToggleButton value="autre" sx={{ flex: '1 1 45%' }}>
              Un autre jour
            </ToggleButton>
          </ToggleButtonGroup>
          {choix === 'autre' && (
            <TextField
              type="date"
              label="Quel jour ?"
              value={autreJour}
              onChange={(e) => setAutreJour(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {jourChoisi !== null && (
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={avecHeure} onChange={(e) => setAvecHeure(e.target.checked)} />
                }
                label="À une heure précise"
              />
              {avecHeure && (
                <TextField
                  type="time"
                  value={heure}
                  onChange={(e) => setHeure(e.target.value)}
                  inputProps={{ 'aria-label': 'Heure' }}
                  sx={{ maxWidth: 140 }}
                />
              )}
            </Stack>
          )}
          <FormControlLabel
            control={<Switch checked={prive} onChange={(e) => setPrive(e.target.checked)} />}
            label="Rien que pour moi"
          />
          {membres.length > 1 && (
            <Box>
              <Button
                color="inherit"
                size="small"
                onClick={() => setPlusOptions((v) => !v)}
                sx={{ px: 0 }}
              >
                {plusOptions ? 'Moins d’options' : 'Plus d’options'}
              </Button>
              <Collapse in={plusOptions}>
                <TextField
                  select
                  label="Qui s’en charge ?"
                  value={responsableId}
                  onChange={(e) => setResponsableId(e.target.value)}
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="">Personne en particulier</MenuItem>
                  {membres.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.prenom}
                    </MenuItem>
                  ))}
                </TextField>
              </Collapse>
            </Box>
          )}
          <Button type="submit" variant="contained" size="large" disabled={!pret || enCours}>
            {element ? 'C’est corrigé' : 'C’est noté'}
          </Button>
          {element && onSupprimer && (
            <Button
              color="error"
              disabled={enCours}
              onClick={() => void onSupprimer().then(onFermer)}
            >
              Supprimer
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
